<template>
  <div>
    <!-- Filters -->
    <b-card>
      <b-row>
        <b-col md="3">
          <b-form-group label="Severity">
            <b-form-select v-model="filterSeverity" :options="severityOptionsTranslated" @change="fetchData" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Farm">
            <b-form-input v-model="filterFarm" placeholder="Filter by farm" @input="fetchData" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Flock">
            <b-form-input v-model="filterFlock" placeholder="Filter by flock" @input="fetchData" />
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="clearFilters">Clear Filters</b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabs -->
    <b-card no-body>
      <b-tabs class="m-2" lazy>
        <b-tab :title="$t('features.alerts.active')" active>
          <!-- Loading skeleton -->
          <div v-if="loading" class="p-2">
            <b-card>
              <b-skeleton width="40%" height="2rem" class="mb-2"/>
              <b-skeleton width="70%" class="mb-1"/>
              <b-skeleton width="55%" class="mb-1"/>
              <b-skeleton-table :rows="5" :columns="4" hide-header/>
            </b-card>
          </div>
          <div v-else-if="!activeAlerts.length" class="text-center py-4 text-muted">
            No active alerts. Great job!
          </div>
          <div v-else class="p-2">
            <b-card
              v-for="alert in activeAlerts"
              :key="alert.id"
              class="mb-2 border-left-alert"
              :class="`border-${severityBorder(alert.severity)}`"
            >
              <b-row>
                <b-col md="8">
                  <div class="d-flex align-items-center mb-1">
                    <b-badge :variant="severityVariant(alert.severity)" class="mr-2">
                      {{ alert.severity | capitalize }}
                    </b-badge>
                    <strong>{{ alert.alert_type }}</strong>
                  </div>
                  <p class="mb-1 text-muted small">Flock: {{ alert.flock_id }} &bull; {{ alert.triggered_at }}</p>
                  <p class="mb-0">{{ alert.description }}</p>
                </b-col>
                <b-col md="4" class="d-flex align-items-center justify-content-end">
                  <b-button variant="success" size="sm" @click="resolveAlert(alert.id)">
                    {{ $t('features.alerts.resolve') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-tab>

        <b-tab title="Resolved Alerts">
          <!-- Loading skeleton -->
          <div v-if="loading" class="p-2">
            <b-card>
              <b-skeleton width="40%" height="2rem" class="mb-2"/>
              <b-skeleton width="70%" class="mb-1"/>
              <b-skeleton-table :rows="5" :columns="4" hide-header/>
            </b-card>
          </div>
          <div v-else-if="!resolvedAlerts.length" class="text-center py-4 text-muted">
            No resolved alerts yet.
          </div>
          <div v-else class="p-2">
            <b-card
              v-for="alert in resolvedAlerts"
              :key="alert.id"
              class="mb-2"
            >
              <b-row>
                <b-col>
                  <div class="d-flex align-items-center mb-1">
                    <b-badge variant="secondary" class="mr-2">Resolved</b-badge>
                    <b-badge :variant="severityVariant(alert.severity)" class="mr-2">
                      {{ alert.severity | capitalize }}
                    </b-badge>
                    <strong>{{ alert.alert_type }}</strong>
                  </div>
                  <p class="mb-1 text-muted small">Flock: {{ alert.flock_id }} &bull; {{ alert.triggered_at }}</p>
                  <p class="mb-0">{{ alert.description }}</p>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BButton, BBadge, BSpinner,
  BTabs, BTab, BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'DiseaseAlertList',
  components: {
    BCard, BRow, BCol, BButton, BBadge, BSpinner,
    BTabs, BTab, BFormGroup, BFormInput, BFormSelect,
  },
  filters: {
    capitalize(val) {
      if (!val) return ''
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
  },
  data() {
    return {
      loading: false,
      allAlerts: [],
      filterSeverity: '',
      filterFarm: '',
      filterFlock: '',
      severityOptions: [],
    }
  },
  computed: {
    severityOptionsTranslated() {
      return [
        { value: '', text: 'All Severities' },
        { value: 'critical', text: this.$t('features.alerts.critical') },
        { value: 'high', text: this.$t('features.alerts.high') },
        { value: 'medium', text: this.$t('features.alerts.medium') },
        { value: 'low', text: this.$t('features.alerts.low') },
      ]
    },
    filteredAlerts() {
      return this.allAlerts.filter(a => {
        if (this.filterSeverity && a.severity !== this.filterSeverity) return false
        if (this.filterFarm && !String(a.farm_id || '').includes(this.filterFarm)) return false
        if (this.filterFlock && !String(a.flock_id || '').includes(this.filterFlock)) return false
        return true
      })
    },
    activeAlerts() {
      return this.filteredAlerts.filter(a => a.status !== 'resolved')
    },
    resolvedAlerts() {
      return this.filteredAlerts.filter(a => a.status === 'resolved')
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const { data } = await axios.get('web/disease-alerts')
        this.allAlerts = data.data || data.items || data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load disease alerts', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    async resolveAlert(id) {
      try {
        await axios.post(`web/disease-alerts/${id}/resolve`)
        this.$bvToast.toast('Alert resolved', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to resolve alert', { variant: 'danger', title: 'Error' })
      }
    },
    severityVariant(severity) {
      const map = { critical: 'danger', high: 'warning', medium: 'info', low: 'primary' }
      return map[severity] || 'secondary'
    },
    severityBorder(severity) {
      const map = { critical: 'danger', high: 'warning', medium: 'info', low: 'primary' }
      return map[severity] || 'secondary'
    },
    clearFilters() {
      this.filterSeverity = ''
      this.filterFarm = ''
      this.filterFlock = ''
    },
  },
}
</script>

<style scoped>
.border-left-alert {
  border-left: 4px solid;
}
</style>
